"use client";

import { useEffect, useState } from "react";
import { Modal, Button, Text, Stack, Group, Divider, Badge, Title } from "@mantine/core";
import { useRouter, usePathname } from "next/navigation";
import { IconRobot, IconBrain } from "@tabler/icons-react";

const MODAL_STORAGE_KEY = "colorAnalysisModalDismissed";
const MODAL_DELAY = 7000; // 7 seconds
const MODAL_COOLDOWN = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export default function ColorAnalysisModal() {
  const [opened, setOpened] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  // NOTE: deliberately NOT shown on /outfits* — those visitors typically
  // already know their season and the page runs its own upsell ladder
  // (outfit generator + Style Club); this modal was competing with it.
  const isOutfitsPage = false;
  const isColorPalettePage = pathname === "/color-palette";
  const isAgeGuesserPage = pathname === "/ai-age-guesser";
  const isGoldOrSilverJewelryTestPage = pathname === "/gold-or-silver-jewelry-test";
  useEffect(() => {
    const checkModalStatus = () => {
      // Only proceed if we're on an outfits page or color-palette page
      if (!isOutfitsPage && !isColorPalettePage && !isAgeGuesserPage && !isGoldOrSilverJewelryTestPage) {
        return;
      }

      const dismissedAt = localStorage.getItem(MODAL_STORAGE_KEY);
      if (dismissedAt) {
        const timeSinceDismissal = Date.now() - parseInt(dismissedAt || "0");
        if (timeSinceDismissal < MODAL_COOLDOWN) {
          return;
        }
      }

      const timer = setTimeout(() => {
        setOpened(true);
      }, MODAL_DELAY);

      return () => clearTimeout(timer);
    };

    checkModalStatus();
  }, [isOutfitsPage, isColorPalettePage, isAgeGuesserPage, isGoldOrSilverJewelryTestPage]);

  const handleDismiss = () => {
    localStorage.setItem(MODAL_STORAGE_KEY, Date.now().toString());
    setOpened(false);
  };

  const handleQuizClick = () => {
    localStorage.setItem(MODAL_STORAGE_KEY, Date.now().toString());
    setOpened(false);
    router.push("/color-analysis-quiz");
  };

  const handleAIClick = () => {
    localStorage.setItem(MODAL_STORAGE_KEY, Date.now().toString());
    setOpened(false);
    router.push("/ai-color-analysis");
  };

  return (
    <Modal
      opened={opened}
      onClose={handleDismiss}
      centered
      size="lg"
      padding="xl"
      title={<Title order={2}>Not sure what your color season is?</Title>}
    >
      <Stack gap="xl">
        <Divider labelPosition="center" />
        <Stack gap="md">
          <Button
            variant="light"
            color="blue"
            onClick={handleQuizClick}
            size="lg"
            fullWidth
            leftSection={<IconBrain size={20} />}
          >
            Take a Free Quiz
          </Button>
          <Text size="sm" c="dimmed" ta="center">
            A fun, quick way to get started with color analysis
          </Text>
        </Stack>

        <Divider label="OR" labelPosition="center" />

        <Stack gap="md">
          <Button
            variant="filled"
            color="violet"
            onClick={handleAIClick}
            size="lg"
            fullWidth
            leftSection={<IconRobot size={20} />}
          >
            AI Color Analysis
          </Button>
          <Group justify="center" gap="xs">
            <Badge color="violet" variant="light">
              Premium
            </Badge>
            <Badge color="violet" variant="light">
              High Quality
            </Badge>
          </Group>
          <Text size="sm" c="dimmed" ta="center">
            Get personalized results with our advanced AI technology
          </Text>
        </Stack>
      </Stack>
    </Modal>
  );
}
